<template>
    <div class="h-full">
        <portal-target name="savedReplyNotificationInputPortal" v-show="showPortalContent"></portal-target>

        <div v-show="!showPortalContent" class="overflow-y-auto overscroll-y-auto scrollbar-thin scrollbar-track-white scrollbar-thumb-indigo-100 pr-4 pl-2"
             style="min-height: 29rem; max-height: 29rem;">
            <!-- DESKTOP VIEW-->
            <portal to="titleDropdownPortal" class="ml-1" v-if="notSmallScreen && !showPortalContent">
                <simple-dropdown :label-class="notSmallScreen ? 'w-44 whitespace-nowrap truncate' : 'w-full whitespace-nowrap truncate'"
                                 :label="selectedCommunicationScenario ? selectedCommunicationScenario.name : 'Choose Scenario' | titlecase">
                    <template slot="buttonOptions">
                        <simple-dropdown-option v-for="scenario in communicationScenarios" class="flex items-center"
                                                :key="scenario.id + 'scenario'" :value="scenario"
                                                @click="selectCommunicationScenario(scenario)">
                            {{ scenario.name | titlecase }}
                            <icon-chip v-if="scenario.is_system" class="pl-1 text-gray-400 h-2 w-2"></icon-chip>
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </portal>

            <!-- MOBILE VIEW -->
            <div class="mt-2" v-if="!notSmallScreen && !showPortalContent">
                <div class="font-medium text-md mb-1">Communication Scenario</div>
                <simple-dropdown :label="selectedCommunicationScenario ? selectedCommunicationScenario.name : 'Choose Scenario' | titlecase"
                                 class="md:w-1/2">
                    <template slot="buttonOptions">
                        <simple-dropdown-option v-for="scenario in communicationScenarios" class="flex items-center"
                                                :key="scenario.id + 'scenario'" :value="scenario"
                                                @click="selectCommunicationScenario(scenario)">
                            {{ scenario.name | titlecase }}
                            <icon-chip v-if="scenario.is_system" class="pl-1 text-gray-400 h-2 w-2"></icon-chip>
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </div>

            <div v-if="state.user.default_mailbox_id === null">
                <p class="text-yellow-700 bg-yellow-100 rounded-md mb-4 p-1 px-2 break-normal">No default mailbox to send an email from. <a
                    class="text-indigo-400 outline-none" :href="'/users/profile/' + app.userId">Click here</a> to select a default mailbox</p>
            </div>

            <!-- Select Service Provider(s)-->
            <div class="w-full mt-2">
                <div class="font-medium bg-white sticky top-0 text-md">Service Providers</div>
                <!-- No Service Providers Selected -->
                <div class="relative bg-white sticky top-0 pb-2" v-show="!statusObject">
                    <remote-select
                        class="mt-1"
                        :disabled="form.busy"
                        ref="remoteSelectServiceProvider"
                        @input="handleSelectServiceProvider"
                        @focus="searchServiceProviders"
                        :remote-method="searchServiceProviders"
                        :data="searchedServiceProviders"
                        :loading="loading"
                        item-label="name"
                        placeholder="Search Service Providers To Task">
                        <template slot-scope="props">
                            <div class="content-group w-full text-xs">
                                <h6 class="flex justify-content-between items-center">
                                    <span class="truncate pr-2">
                                        <span class="flex items-center  ">
                                            <span class="mr-1"> {{ props.item.name }}</span>
                                            <template v-if="props.item.primary_contact">
                                                <span class="text-indigo-600">&middot;</span>
                                                <span class="ml-1 text-gray-600 font-bold">
                                                    {{ props.item.primary_contact.contact_name }}
                                                </span>
                                            </template>
                                        </span>
                                    </span>

                                    <span class="flex items-center">
                                        <icon-registered-service-provider class="text-indigo-600 h-4 w-4"
                                                                          v-if="props.item.is_registered_company && hasFeature('feat_service_provider_portals')">
                                        </icon-registered-service-provider>

                                        <badge class="ml-2 bg-indigo-50 text-indigo-600">
                                            {{ props.item.type | titlecase }}
                                        </badge>
                                    </span>
                                </h6>
                            </div>
                        </template>
                    </remote-select>
                    <div v-if="form.service_providers.length === 0" class="py-8 text-gray-400 font-base flex items-center justify-center">
                        No service providers selected yet
                    </div>
                </div>

                <!-- Selected Service Providers-->
                <transition name="slide">
                    <div v-show="form.service_providers && form.service_providers.length > 0" class="mt-2 space-y-4">
                        <ul class="space-y-4">
                            <li v-for="(serviceProvider, spIndex) in form.service_providers" :key="serviceProvider.id"
                                class="flex flex-col p-4 bg-indigo-50 rounded-md">
                                <!-- Service Provider Details -->
                                <div class="flex items-center justify-between mb-2">
                                    <div class="flex flex-col justify-start">
                                        <div class="flex items-center">
                                            <div class="mr-1 font-medium leading-none flex items-center">
                                                <span class="text-indigo-600">
                                                    {{ serviceProvider.name }}
                                                </span>
                                                <template v-if="serviceProvider.primary_contact">
                                                    <span class="mx-2">&middot;</span>
                                                    <span>{{ serviceProvider.primary_contact.contact_name }}</span>
                                                </template>
                                            </div>

                                            <icon-registered-service-provider class="text-indigo-600 h-3 w-3 mb-0.5"
                                                                              v-if="serviceProvider.is_registered_company && hasFeature('feat_service_provider_portals')">
                                            </icon-registered-service-provider>
                                        </div>
                                    </div>

                                    <div class="flex items-center h-full" v-if="!statusObject">
                                        <button class="outline-none text-gray-400 hover:text-brand transition duration-300"
                                                :disabled="form.busy"
                                                @click.prevent="removeServiceProviderFromSelectedArray(serviceProvider.id)">
                                            <icon-x-circle class="w-4 h-4"></icon-x-circle>
                                        </button>
                                    </div>
                                </div>

                                <!-- Communication Channels -->
                                <template v-if="serviceProvider.primary_contact">
                                    <div class="flex flex-col space-y-2" v-if="serviceProvider && serviceProvider.communication_channels">
                                        <div class="flex flex-col" v-for="(commChannel, commIndex) in serviceProvider.communication_channels">
                                            <div v-if="(state.user.default_mailbox_id !== null && commChannel.type === type_email) ||
                                                 commChannel.type === type_sms || commChannel.type === type_messenger">
                                                <saved-reply-notification-input :type="commChannel.type" v-model="commChannel.saved_reply"
                                                                                role="service provider" @toggle-edit-portal="toggleEditPortal()"
                                                                                :to-email="serviceProvider && serviceProvider.primary_contact ? serviceProvider.primary_contact.email : null"
                                                                                :key="commIndex + serviceProvider.id"
                                                                                :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId,  service_provider_id: serviceProvider.id}"
                                                                                :edit-message="ticketId ? '' : 'Please note that the Saved Reply tags will be replaced during ticket creation'"
                                                                                @input="clearNoCommunicationChannelError(spIndex); clearServiceProviderNotificationErrors(spIndex, commIndex)">
                                                </saved-reply-notification-input>
                                            </div>
                                            <div v-else-if="state.user.default_mailbox_id === null && commChannel.type === type_email" class="font-medium">
                                                You do not have a default mailbox
                                            </div>

                                            <!-- Error for when issues with saved reply -->
                                            <div class="flex items-center justify-end" v-show="!form.errors.has(`service_providers.${spIndex}.communication_channels`) &&
                                  form.errors.has(`service_providers.${spIndex}.communication_channels.${commIndex}.saved_reply.custom_body`)">
                                                <span class="text-red-500 text-sm mt-1 text-right"
                                                      v-if="!form.errors.has(`service_providers.${spIndex}.communication_channels`) &&
                                                       form.errors.has(`service_providers.${spIndex}.communication_channels.${commIndex}.saved_reply.custom_body`)">
                                                    {{ form.errors.get(`service_providers.${spIndex}.communication_channels.${commIndex}.saved_reply.custom_body`) }}
                                                </span>
                                                <span class="text-red-500 text-sm mt-1 text-right"
                                                      v-if="!form.errors.has(`service_providers.${spIndex}.communication_channels`) &&
                                                       form.errors.has(`service_providers.${spIndex}.communication_channels.${commIndex}.saved_reply.media`)">
                                                    {{ form.errors.get(`service_providers.${spIndex}.communication_channels.${commIndex}.saved_reply.media`) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <div class="font-medium text-md">Service Provider does not have a primary contact set</div>
                                    <div class="flex items-center space-x-1">
                                        <a :href="`/serviceproviders/${serviceProvider.id}`" target="_blank"
                                           @click="removeServiceProviderFromSelectedArray(serviceProvider.id)"
                                           class="text-sm text-indigo-600 hover:text-indigo-800 transition duration-300">
                                            Click here to set a primary contact,
                                        </a>
                                        <div class="text-sm">then re-add the service provider to the list
                                        </div>
                                    </div>
                                </template>

                                <!-- Error for when no communication channel is selected-->
                                <div class="flex items-center" v-show="form.errors.has(`service_providers.${spIndex}.communication_channels`)">
                                    <span class="text-red-500 text-sm mt-1" v-show="form.errors.has(`service_providers.${spIndex}.communication_channels`)">
                                        {{ form.errors.get(`service_providers.${spIndex}.communication_channels`) }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </transition>

                <!-- Error for when no service provider is selected -->
                <span class="text-red-500 text-sm mt-1" v-show="form.errors.has('service_providers')">
                    {{ form.errors.get("service_providers") }}
                </span>
            </div>

            <!-- Select Ticket Tenant and Landlord Notifications-->
            <div class="mt-8">
                <div class="font-medium text-md">Notify Contacts</div>

                <div v-if="form && !ticketId" class="pt-4">
                    <div v-if="!form.ticket_details.asset_id" class="font-medium text-md">
                        Please select an asset to select the ticket opened notification settings
                    </div>

                    <div v-else-if="!selectedTenant && !selectedLandlord" class="font-medium text-md">
                        Cannot choose ticket opened notifications as there is no selected tenant and the asset does not have a primary landlord
                    </div>
                </div>

                <div v-if="variableToRenderInputs">
                    <transition-group name="slide">
                        <!-- When creating ticket -->
                        <ul v-show="(selectedTenant || selectedLandlord)"
                            class="space-y-4 mt-2" key="selectedNotifications">
                            <li class="flex flex-col p-4 bg-indigo-50 rounded-md space-y-2" v-if="selectedTenant">
                                <!-- Tenant Details -->
                                <div class="flex flex-col md:flex-row md:justify-between md:items-center">
                                    <div class="flex flex-col order-2 md:order-1">
                                        <div class="mr-1 font-medium flex items-center">
                                            <span class="text-indigo-600">
                                                Selected Tenant
                                            </span>
                                            <span class="mx-2">&middot;</span>
                                            <span>{{ selectedTenant.full_name }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-2">
                                    <div v-if="!disableTenantEmailOption">
                                        <saved-reply-notification-input type="email" role="tenant" @input="clearContactNotificationErrors('tenant', 'email')"
                                                                        :to-email="selectedTenant && selectedTenant.email ? selectedTenant.email : null"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: selectedTenant.id}"
                                                                        v-model="form.ticket_notification_settings.tenant_email_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_email_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_email_communication.custom_body`) }}
                                        </span>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_email_communication.media`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_email_communication.media`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">
                                        {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Selected tenant has no valid email address" }}
                                    </div>

                                    <div v-if="!disableTenantSMSOption">
                                        <saved-reply-notification-input type="sms" role="tenant" @input="clearContactNotificationErrors('tenant', 'sms')"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: selectedTenant.id}"
                                                                        v-model="form.ticket_notification_settings.tenant_sms_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_sms_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_sms_communication.custom_body`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">Selected tenant has no valid mobile number</div>
                                </div>
                            </li>

                            <li v-else class="text-md font-medium">You have not selected a tenant</li>

                            <li class="flex flex-col p-4 bg-indigo-50 rounded-md space-y-2" v-if="selectedLandlord">
                                <!-- Landlord Details -->
                                <div class="flex flex-col md:flex-row md:justify-between md:items-center">
                                    <div class="flex flex-col order-2 md:order-1">
                                        <div class="mr-1 font-medium flex items-center">
                                            <span class="text-indigo-600">
                                                Primary Landlord
                                            </span>
                                            <span class="mx-2">&middot;</span>
                                            <span>{{ selectedLandlord.full_name }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-2">
                                    <div v-if="!disableLandlordEmailOption">
                                        <saved-reply-notification-input type="email" role="landlord" @input="clearContactNotificationErrors('landlord', 'email')"
                                                                        :to-email="selectedLandlord && selectedLandlord.email ? selectedLandlord.email : null"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: selectedLandlord.id}"
                                                                        v-model="form.ticket_notification_settings.landlord_email_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_email_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_email_communication.custom_body`) }}
                                        </span>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_email_communication.media`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_email_communication.media`) }}
                                        </span>
                                    </div>
                                    <div v-else
                                         class="font-medium">
                                        {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Asset's primary landlord has no valid email address" }}
                                    </div>

                                    <div v-if="!disableLandlordSMSOption">
                                        <saved-reply-notification-input type="sms" role="landlord" @input="clearContactNotificationErrors('landlord', 'sms')"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: selectedLandlord.id}"
                                                                        v-model="form.ticket_notification_settings.landlord_sms_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_sms_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_sms_communication.custom_body`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">Asset's primary landlord has no valid mobile number</div>
                                </div>
                            </li>

                            <li v-else class="text-md font-medium">There is no primary landlord for the selected asset</li>
                        </ul>

                        <!-- When on ticket -->
                        <ul v-show="(primaryTenant || primaryLandlord)"
                            class="space-y-4 mt-2" key="primaryNotifications">
                            <li class="flex flex-col p-4 bg-indigo-50 rounded-md space-y-2" v-if="primaryTenant">
                                <!-- Tenant Details -->
                                <div class="flex flex-col md:flex-row md:justify-between md:items-center">
                                    <div class="flex flex-col order-2 md:order-1">
                                        <div class="mr-1 font-medium flex items-center">
                                            <span class="text-indigo-600">
                                                Primary Tenant
                                            </span>
                                            <span class="mx-2">&middot;</span>
                                            <span>{{ primaryTenant.full_name }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-2">
                                    <div v-if="!disableTenantEmailOption">
                                        <saved-reply-notification-input type="email" role="tenant" @input="clearContactNotificationErrors('tenant', 'email')"
                                                                        :to-email="primaryTenant && primaryTenant.email ? primaryTenant.email : null"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: primaryTenant.id}"
                                                                        v-model="form.ticket_notification_settings.tenant_email_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_email_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_email_communication.custom_body`) }}
                                        </span>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_email_communication.media`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_email_communication.media`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">
                                        {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Primary tenant has no valid email address" }}
                                    </div>

                                    <div v-if="!disableTenantSMSOption">
                                        <saved-reply-notification-input type="sms" role="tenant" @input="clearContactNotificationErrors('tenant', 'sms')"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: primaryTenant.id}"
                                                                        v-model="form.ticket_notification_settings.tenant_sms_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.tenant_sms_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.tenant_sms_communication.custom_body`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">Primary tenant has no valid mobile number</div>
                                </div>
                            </li>

                            <li v-else class="text-md font-medium">Ticket does not have a primary tenant</li>

                            <li class="flex flex-col p-4 bg-indigo-50 rounded-md space-y-2" v-if="primaryLandlord">
                                <!-- Landlord Details -->
                                <div class="flex flex-col md:flex-row md:justify-between md:items-center">
                                    <div class="flex flex-col order-2 md:order-1">
                                        <div class="mr-1 font-medium flex items-center">
                                            <span class="text-indigo-600">
                                                Primary Landlord
                                            </span>
                                            <span class="mx-2">&middot;</span>
                                            <span>{{ primaryLandlord.full_name }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-2">
                                    <div v-if="!disableLandlordEmailOption">
                                        <saved-reply-notification-input type="email" role="landlord" @input="clearContactNotificationErrors('landlord', 'email')"
                                                                        :to-email="primaryLandlord && primaryLandlord.email ? primaryLandlord.email : null"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: primaryLandlord.id}"
                                                                        v-model="form.ticket_notification_settings.landlord_email_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_email_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_email_communication.custom_body`) }}
                                        </span>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_email_communication.media`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_email_communication.media`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">
                                        {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Ticket's primary landlord has no valid email address" }}
                                    </div>

                                    <div v-if="!disableLandlordSMSOption">
                                        <saved-reply-notification-input type="sms" role="landlord" @input="clearContactNotificationErrors('landlord', 'sms')"
                                                                        :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticketId, selectedContact: primaryLandlord.id}"
                                                                        v-model="form.ticket_notification_settings.landlord_sms_communication"
                                                                        @toggle-edit-portal="toggleEditPortal()">
                                        </saved-reply-notification-input>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`ticket_notification_settings.landlord_sms_communication.custom_body`)">
                                            {{ form.errors.get(`ticket_notification_settings.landlord_sms_communication.custom_body`) }}
                                        </span>
                                    </div>
                                    <div v-else class="font-medium">Ticket's primary landlord has no valid mobile number</div>
                                </div>
                            </li>

                            <li v-else class="text-md font-medium">There is no primary landlord linked to this ticket</li>
                        </ul>
                    </transition-group>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LoadSavedReplies from "../../../mixins/load-email-saved-replies";
import ContactCommunicationMixin from "../../../mixins/contact-communication-mixin";
import CommunicationScenarioMixin from "../../../mixins/communication-scenario-mixin";

export default {
    name: "TaskServiceProviderModalCommunication",

    mixins: [LoadSavedReplies, ContactCommunicationMixin, CommunicationScenarioMixin],

    props: {
        form: {
            required: true,
            type: Object,
        },
        selectedTenant: {
            required: false,
            type: Object,
            default: null,
            description: "Will only be passed through when creating a ticking, not when tasking a service provider on a ticket",
        },
        selectedLandlord: {
            required: false,
            type: Object,
            default: null,
            description: "Will only be passed through when creating a ticking, not when tasking a service provider on a ticket",
        },
        communicationScenarios: {
            required: true,
            type: Array,
            description: "Will be used to load the default communication for a specific scenario"
        },
        ticketId: {
            required: false,
            type: Number,
            default: null,
            Description: "Check if we are using this component on a ticket or when creating a ticket",
        },
        maintenanceRequestId: {
            required: false,
            type: Number,
            default: null,
            Description: "Check if we are opening the ticket from a maintenance request",
        },
        statusObject: {
            required: false,
            type: Object,
            default: null,
            description: "Used when Quote Approved/Rejected button is used to limit certain actions"
        },
        gotFocus: {
            required: false,
            type: Boolean,
            default: false,
            description: "Used when previous then next buttons was clicked to trigger on focus events"
        }
    },

    data () {
        return {
            loading: false,
            searchedServiceProviders: [],
            primaryTenant: null,
            primaryLandlord: null,
            variableToRenderInputs: true,
            showPortalContent: false,
        };
    },

    computed: {
        disableTenantEmailOption () {
            return this.validateContactEmail(this.selectedTenant ? this.selectedTenant : this.primaryTenant);
        },

        disableTenantSMSOption () {
            return (!this.selectedTenant || _.isEmpty(this.selectedTenant.mobile)) && (!this.primaryTenant || _.isEmpty(this.primaryTenant.mobile));
        },

        disableLandlordEmailOption () {
            return this.validateContactEmail(this.selectedLandlord ? this.selectedLandlord : this.primaryLandlord);
        },

        disableLandlordSMSOption () {
            return (!this.selectedLandlord || _.isEmpty(this.selectedLandlord.mobile)) && (!this.primaryLandlord || _.isEmpty(this.primaryLandlord.mobile));
        },
    },

    watch: {
        selectedTenant (newVal) {
            if (newVal && this.form && this.form.ticket_notification_settings) {
                this.$nextTick(function () {
                    this.setTenantCommunication();
                });
            }
        },

        selectedLandlord (newVal) {
            if (newVal && this.form && this.form.ticket_notification_settings) {
                this.$nextTick(function () {
                    this.setLandlordCommunication();
                });
            }
        },

        "form.service_providers": function (serviceProviders) {
            let self = this;
            serviceProviders.forEach(sp => {
                self.setServiceProviderCommunicationTypes(sp);
            });
            if (this.form.errors.has("service_providers")) {
                this.form.errors.forget("service_providers");
            }
        },

        // forces rerender of tenant and landlord notifications after tasking
        "form.ticket_notification_settings": function (object) {
            this.variableToRenderInputs = !(object && object.reanimate);
        },

        statusObject (statusObject) {
            this.preSelectedScenarioName = "tasking service provider";
            if (statusObject) {
                if (statusObject.status === "quote approved") {
                    this.preSelectedScenarioName = "approving quote";
                } else if (statusObject.status === "quote rejected") {
                    this.preSelectedScenarioName = "rejecting quote";
                }
            } else {
                this.preSelectedScenarioName = "requesting quote";
            }
        },

        gotFocus (gotFocus) {
            if (gotFocus && !this.selectedCommunicationScenario) {
                this.$emit("disableNextButton", true);
            }
        },
    },

    mounted () {
        if (this.ticketId) {
            this.fetchPrimaryContactsFromTicket();
        }

        if (this.maintenanceRequestId) {
            this.preSelectedScenarioName = "opening ticket from maintenance request";
        }
    },

    methods: {
        checkRegisteredCompany (serviceProvider) {
            return serviceProvider.is_registered_company && this.hasFeature("feat_service_provider_portals");
        },

        setServiceProviderCommunicationTypes (serviceProvider) {
            this.$nextTick(function () {
                this.$set(serviceProvider, "communication_channels", []);
                this.communication_channel_types.forEach(type => {
                    // Do not give type of messenger if not registered company, communication channels will be looped by type above
                    if (type === "messenger" && !(this.checkRegisteredCompany(serviceProvider))) {
                        return;
                    }

                    let key = "service_provider_" + type;
                    serviceProvider.communication_channels.push({
                        type: type,
                        saved_reply: _.cloneDeep(this.defaultCommunication[key])
                    });
                });
            });
        },

        searchServiceProviders (query) {
            // If query is empty element passes element instance
            // instead of string - so we make it a string
            if (!_.isString(query)) {
                query = "";
            }

            this.loading = true;

            axios.get("/serviceproviders/search", {params: {filter_text: query}})
            .then(response => {
                this.searchedServiceProviders = response.data.data;
                this.loading = false;
            }).catch(error => {
                this.loading = false;
                toastr.error("Error loading service providers. Error" + error);
            });
        },

        handleSelectServiceProvider (chosenProvider) {
            // Remove from array if already added, otherwise add
            if (chosenProvider && !this.removeServiceProviderFromSelectedArray(chosenProvider.id)) {
                this.form.service_providers.push(chosenProvider);
            }
            // Automatically close remote select
            this.$refs.remoteSelectServiceProvider.selectedItem = null;
            this.$refs.remoteSelectServiceProvider.showItemsPopover = false;
        },

        removeServiceProviderFromSelectedArray (serviceProviderId) {
            let index = this.form.service_providers.findIndex(sp => sp.id === serviceProviderId);
            if (index !== -1) {
                this.form.service_providers.splice(index, 1);
                return true;
            }
            return false;
        },

        clearNoCommunicationChannelError (index) {
            // Refer to StoreTicketServiceProviders.php
            if (this.form.errors.has(`service_providers.${index}.communication_channels`)) {
                this.form.errors.forget(`service_providers.${index}.communication_channels`);
            }
        },

        clearServiceProviderNotificationErrors (serviceProviderIndex, commIndex) {
            // Refer to StoreTicketServiceProviders.php
            if (this.form.errors.has(`service_providers.${serviceProviderIndex}.communication_channels.${commIndex}.saved_reply.custom_body`)) {
                this.form.errors.forget(`service_providers.${serviceProviderIndex}.communication_channels.${commIndex}.saved_reply.custom_body`);
            }
        },

        clearContactNotificationErrors (role, commType) {
            if (this.form.errors.has(`ticket_notification_settings.${role}_${commType}_communication.custom_body`)) {
                this.form.errors.forget(`ticket_notification_settings.${role}_${commType}_communication.custom_body`);
            }
        },

        fetchPrimaryContactsFromTicket () {
            axios.get(`/contacts/primaryTenant`, {
                params: {
                    contactable: "App\\Ticket",
                    contactable_id: this.ticketId,
                }
            })
            .then(response => {
                this.primaryTenant = response.data[0];
            }).catch(error => {
                toastr.error("Could not fetch primary tenant Error: " + error.message);
            });

            axios.get(`/contacts/primaryLandlord`, {
                params: {
                    contactable: "App\\Ticket",
                    contactable_id: this.ticketId,
                }
            })
            .then(response => {
                this.primaryLandlord = response.data[0];
            }).catch(error => {
                toastr.error("Could not fetch primary landlord. Error: " + error.message);
            });
        },

        selectCommunicationScenario (scenario) {
            this.selectedCommunicationScenario = scenario;

            if (this.selectedCommunicationScenario && this.selectedCommunicationScenario.saved_replies) {
                this.$set(this.form, "communication_scenario_type", this.selectedCommunicationScenario.scenario);
                this.defaultCommunication = this.getSavedReplies(this.selectedCommunicationScenario);
                this.setTenantCommunication();
                this.setLandlordCommunication();
                this.setServiceProviderCommunication();
            }

            this.$emit("disableNextButton", false);
        },

        setTenantCommunication () {
            //reset v-model
            this.$set(this.form.ticket_notification_settings, "tenant_email_communication", {refresh: true});
            this.$set(this.form.ticket_notification_settings, "tenant_sms_communication", {refresh: true});

            this.$nextTick(function () {
                this.$set(this.form.ticket_notification_settings, "tenant_email_communication", this.disableTenantEmailOption ? null : _.cloneDeep(this.defaultCommunication.tenant_email));
                this.$set(this.form.ticket_notification_settings, "tenant_sms_communication", this.disableTenantSMSOption ? null : _.cloneDeep(this.defaultCommunication.tenant_sms));
            });
        },

        setLandlordCommunication () {
            //reset v-model
            this.$set(this.form.ticket_notification_settings, "landlord_email_communication", {refresh: true});
            this.$set(this.form.ticket_notification_settings, "landlord_sms_communication", {refresh: true});

            this.$nextTick(function () {
                this.$set(this.form.ticket_notification_settings, "landlord_email_communication", this.disableLandlordEmailOption ? null : _.cloneDeep(this.defaultCommunication.landlord_email));
                this.$set(this.form.ticket_notification_settings, "landlord_sms_communication", this.disableLandlordSMSOption ? null : _.cloneDeep(this.defaultCommunication.landlord_sms));
            });
        },

        setServiceProviderCommunication () {
            let self = this;
            if (this.form && this.form.service_providers && this.form.service_providers.length > 0) {
                this.form.service_providers.forEach(sp => {
                    self.setServiceProviderCommunicationTypes(sp);
                });
            }
        },

        toggleEditPortal () {
            this.showPortalContent = !this.showPortalContent;
            this.$emit("toggle-close-button");
        },
    },
};
</script>
